/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React from 'react';
import envConfig from '../config/envConfig';
import { useAppBranding } from '../hooks/useAppBranding';
import { useTheme } from '../theme/ThemeContext';
import Button from './Button';
import VelLogo from './VelLogo';
import '../styles/Header.css';
import { useNavigate } from 'react-router-dom';

function Header() {
    const { theme } = useTheme();
    const { data: branding } = useAppBranding();
    const navigate = useNavigate();

    const handleSignInClick = () => {
        navigate('/sign-in');
    };

    const handleSignUpClick = () => {
        navigate('/sign-up');
    };

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <nav
            className="navbar navbar-expand-lg navbar-light py-3"
            style={{
                background: 'white',
                boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                position: 'sticky',
                top: 0,
                zIndex: 1000
            }}
        >
            <div className="container-fluid px-4">
                <a
                    className="navbar-brand d-flex align-items-center gap-2"
                    href="/"
                    style={{
                        color: theme.primary,
                        fontWeight: 800,
                        fontSize: '1.6rem',
                        textDecoration: 'none'
                    }}
                >
                    {branding?.logo ? (
                        <img
                            src={branding.logo}
                            alt={envConfig.appName}
                            style={{ height: 40 }}
                        />
                    ) : (
                        <VelLogo size={40} />
                    )}
                    <span className="arcee-logo-text">{envConfig.appName}</span>
                </a>

                {/* Hamburger for mobile */}
                <button
                    className="navbar-toggler"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#headerNav"
                    aria-controls="headerNav"
                    aria-expanded="false"
                    aria-label="Toggle navigation"
                    style={{ border: `2px solid ${theme.primary}` }}
                >
                    <span className="navbar-toggler-icon"></span>
                </button>

                <div className="collapse navbar-collapse" id="headerNav">
                    <ul className="navbar-nav mx-auto mb-2 mb-lg-0">
                        <li className="nav-item">
                            <button
                                className="nav-link-btn"
                                onClick={() => scrollToSection('about')}
                            >
                                Platform
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className="nav-link-btn"
                                onClick={() => scrollToSection('features')}
                            >
                                Features
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className="nav-link-btn"
                                onClick={() => scrollToSection('use-cases')}
                            >
                                Use Cases
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className="nav-link-btn"
                                onClick={() => scrollToSection('roadmap')}
                            >
                                Roadmap
                            </button>
                        </li>
                    </ul>
                    <div className="d-flex align-items-center gap-2">
                        {envConfig.showExploreButton && (
                            <Button
                                mode="bordered"
                                className="w-lg-auto"
                                onClick={() => navigate('/p/event')}
                            >
                                <i className="bi bi-globe2 me-1" />
                                Explore
                            </Button>
                        )}
                        {envConfig.isUserLoginAllowed && (
                            <Button
                                mode="primary"
                                className="w-lg-auto"
                                onClick={handleSignInClick}
                            >
                                Sign In
                            </Button>
                        )}
                        {envConfig.isUserRegistrationAllowed && (
                            <Button
                                mode="bordered"
                                className="w-lg-auto"
                                onClick={handleSignUpClick}
                            >
                                Sign Up
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </nav>
    );
}

export default Header;